/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';

import { Popup } from '@atlaskit/popup/popup';
import type { TriggerProps } from '@atlaskit/popup/types';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss/xcss';

import { useDropdownPrefetch } from '../../../controllers/dropdown';
import { ErrorBoundary } from '../../../controllers/error-boundary';
import { useDropdown, useStyleOverrides } from '../../../controllers/store';
import { SearchDropdown } from './dropdown';
import { SearchTextField } from './textfield';

const textFieldBoxStyles = xcss({
	height: 'space.500',
	flexGrow: 1,
});

export const Input = (): JSX.Element => {
	const [showDropdown, { setShowDropdown }] = useDropdown();
	const inputBoxRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [styleOverrides] = useStyleOverrides();

	useDropdownPrefetch();

	const [_, setInputWidth] = useState(inputBoxRef.current?.offsetWidth);

	useEffect(() => {
		const listener = () => setInputWidth(inputBoxRef.current?.offsetWidth);
		const unbind = bind(window, {
			type: 'resize',
			listener,
		});
		return unbind;
	}, []);

	const closeDropdown = useCallback(() => {
		if (showDropdown) {
			setShowDropdown(false);
		}
	}, [showDropdown, setShowDropdown]);

	const trigger = useCallback(
		(triggerProps: TriggerProps) => {
			// When the SearchTextField receives a submit/enter
			const hasSubmitted = () => {
				closeDropdown();
			};

			// When the SearchTextField receives focus - simple API for multiple events that cause this
			const inputHasReceivedFocus = () => {
				if (!showDropdown) {
					setShowDropdown(true);
				}
			};

			return (
				<Box xcss={textFieldBoxStyles} ref={inputBoxRef}>
					<SearchTextField
						keyboardRef={inputRef}
						{...triggerProps}
						ref={triggerProps.ref}
						onSubmit={hasSubmitted}
						hasReceivedFocus={inputHasReceivedFocus}
						styleOverrides={styleOverrides?.textfield}
					/>
				</Box>
			);
		},
		[closeDropdown, showDropdown, setShowDropdown, styleOverrides?.textfield],
	);

	const content = <SearchDropdown inputWidth={inputBoxRef.current?.offsetWidth || 0} />;

	return (
		<Popup
			shouldReturnFocus={false}
			content={() => content}
			isOpen={showDropdown}
			autoFocus={false}
			placement="bottom-start"
			trigger={trigger}
			onClose={closeDropdown}
		/>
	);
};

const InputWithErrorBoundary = (): JSX.Element => {
	return (
		<ErrorBoundary fallback={<SearchTextField />}>
			<Input />
		</ErrorBoundary>
	);
};

export default InputWithErrorBoundary;
